import moment from 'moment';
import { FC, memo } from 'react';
import { Area, AreaChart, CartesianGrid, Tooltip } from 'recharts';

import { theme, Typography } from '@/design-system';
import { formatWithoutFraction } from '@/utils/numbers/formatters';

import { ChartTooltip } from './performance-chart.styled';

interface IProps {
  data: Array<{ date: string | number; value: number }>;
  width: number;
  height: number;
  format: string;
  lineWidth?: number;
}

const Chart: FC<IProps> = ({ data, width, height, format, lineWidth = 3 }) => (
  <AreaChart width={width} height={height} data={data}>
    <defs>
      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
        <stop stopColor={theme.colors.brand.solid} stopOpacity={0.15} />
        <stop offset={1} stopColor={theme.colors.brand.solid} stopOpacity={0} />
      </linearGradient>
    </defs>

    <CartesianGrid vertical horizontal={false} stroke={theme.colors.border.default} />

    <Tooltip
      content={({ active, payload }) => {
        if (!active || !payload?.length) return null;

        const point = payload[0].payload as { date: string; value: number };
        return (
          <ChartTooltip>
            <Typography variant="body3" fontWeight={700} color={theme.colors.text.brand}>
              {`${format}${formatWithoutFraction(point.value)}`}
            </Typography>
            <Typography variant="body4" color={theme.colors.text.secondary}>
              {moment(point.date).format('D MMMM YYYY, HH:mm')}
            </Typography>
          </ChartTooltip>
        );
      }}
    />

    <Area
      dataKey="value"
      type="monotone"
      stroke={theme.colors.brand.solid}
      strokeWidth={lineWidth}
      fill="url(#colorValue)"
      isAnimationActive={false}
      dot={false}
      activeDot={{ r: 5, strokeWidth: 2 }}
    />
  </AreaChart>
);

export const PerformanceChart = memo(Chart);
